import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	RemixContainer,
	type RemixOptionSelectionParameters,
} from '@atlassian/malleable-ui-components';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { RendererRemixHighlightStyles } from './RendererRemixHighlightStyles.tsx';
import { useRendererRemixHighlight } from './useRendererRemixHighlight.ts';

import type { DynamicUiSource } from './common-types.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';

type RendererRemixCreatorProps = {
	selectedContext: string;
	onClose: (options?: { preserveHighlight?: boolean }) => void;
};

const RendererRemixModal = ({ selectedContext, onClose }: RendererRemixCreatorProps) => {
	const { sendAIFCPrompt, openAIFCStagingModal } = useRemixToAIFC();

	const handleOptionSelect = useCallback(
		async ({ type, userPrompt }: RemixOptionSelectionParameters) => {
			// TOOD: This should be created from selectedContext
			const dynamicUiSource = {} as DynamicUiSource;
			await sendAIFCPrompt({
				selectedText: selectedContext,
				userPrompt,
				dynamicUiType: type,
				dynamicUiSource,
			});
			openAIFCStagingModal();
			onClose({ preserveHighlight: true });
		},
		[onClose, sendAIFCPrompt, openAIFCStagingModal, selectedContext],
	);

	return (
		<RemixContainer selectedText="" onClose={() => onClose()} onOptionSelect={handleOptionSelect} />
	);
};

type RendererRemixContextProps = {
	openRemixModal: (selection: string) => void;
	clearRemixHighlight: () => void;
};
const RendererRemixContext = createContext<RendererRemixContextProps>({
	openRemixModal: () => {},
	clearRemixHighlight: () => {},
});

export const useRendererRemixContext = () => {
	return useContext(RendererRemixContext);
};

const RendererRemixContainerImpl = ({ children }: { children: React.ReactNode }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedContext, setSelectedContext] = useState<string | undefined>(undefined);
	const { startHighlight, clearHighlight } = useRendererRemixHighlight();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const prevShowSmartCreationModal = useRef(showSmartCreationModal);

	useEffect(() => {
		if (prevShowSmartCreationModal.current && !showSmartCreationModal) {
			clearHighlight();
		}
		prevShowSmartCreationModal.current = showSmartCreationModal;
	}, [showSmartCreationModal, clearHighlight]);

	const openModal = useCallback(
		(selection: string) => {
			startHighlight();
			setSelectedContext(selection);
			setIsModalOpen(true);
		},
		[startHighlight],
	);

	const closeModal = useCallback(
		(options?: { preserveHighlight?: boolean }) => {
			if (!options?.preserveHighlight) {
				clearHighlight();
			}
			setSelectedContext(undefined);
			setIsModalOpen(false);
		},
		[clearHighlight],
	);

	const context = useMemo(() => {
		return { openRemixModal: openModal, clearRemixHighlight: clearHighlight };
	}, [openModal, clearHighlight]);

	return (
		<RendererRemixContext.Provider value={context}>
			<RendererRemixHighlightStyles />
			{children}
			{isModalOpen && selectedContext && (
				<ConfluenceConversationAssistant>
					<RendererRemixModal selectedContext={selectedContext} onClose={closeModal} />
				</ConfluenceConversationAssistant>
			)}
		</RendererRemixContext.Provider>
	);
};

export const RendererRemixContainer = ({ children }: { children: React.ReactNode }) => {
	if (!expVal('cc-maui-experiment', 'isEnabled', false)) {
		return <>{children}</>;
	}

	return <RendererRemixContainerImpl>{children}</RendererRemixContainerImpl>;
};
